import { Network } from 'lucide-react'
import { useMemo } from 'react'
import type { GraphViewEdge, GraphViewNode } from '@/types/graph-view'
import { cn } from '@/utils/cn'
import { entityColorClass } from './entity-color'

interface GraphMiniPreviewProps {
  nodes: GraphViewNode[]
  edges: GraphViewEdge[]
  className?: string
  /** Max nodes laid out. Extras still counted but not drawn. */
  limit?: number
  /** Max legend chips shown. Extras roll up into a "+N" tail chip. */
  legendLimit?: number
}

const VB_W = 200
const VB_H = 138

/**
 * Lightweight SVG preview of an investigation subgraph.
 *
 * Nodes are placed in concentric rings around the highest-degree hub using a
 * deterministic radial layout — no force simulation, no async layout, safe to
 * render many cards on one page. A legend strip below the SVG explains each
 * entity colour so the preview is readable without hovering individual dots.
 */
export function GraphMiniPreview({
  nodes,
  edges,
  className,
  limit = 24,
  legendLimit = 4,
}: GraphMiniPreviewProps) {
  const layout = useMemo(() => computeLayout(nodes, edges, limit), [nodes, edges, limit])
  const legend = useMemo(() => buildLegend(layout.nodes), [layout.nodes])
  const hasData = nodes.length > 0
  const shownLegend = legend.slice(0, legendLimit)
  const hiddenLegend = legend.length - shownLegend.length

  return (
    <div
      data-empty={hasData ? undefined : ''}
      className={cn(
        'relative flex flex-col overflow-hidden rounded-lg border border-border/40 bg-muted/30',
        className
      )}
    >
      <div className="absolute left-2 top-2 z-10 flex items-center gap-1 text-3xs font-medium uppercase tracking-wider text-muted-foreground">
        <Network className="size-3" />
        Graph
      </div>
      {hasData ? (
        <svg
          viewBox={`0 0 ${VB_W} ${VB_H}`}
          className="block h-full w-full"
          preserveAspectRatio="xMidYMid meet"
        >
          <g className="text-border" stroke="currentColor" strokeWidth={0.6} opacity={0.7}>
            {layout.edges.map(e => (
              <line key={e.id} x1={e.x1} y1={e.y1} x2={e.x2} y2={e.y2} />
            ))}
          </g>
          <g>
            {layout.nodes.map(n => (
              <circle
                key={n.id}
                cx={n.x}
                cy={n.y}
                r={n.r}
                className={cn(entityColorClass(n.label), 'transition-opacity')}
                fill="currentColor"
                fillOpacity={0.85}
                stroke="currentColor"
                strokeWidth={0.8}
              />
            ))}
          </g>
        </svg>
      ) : (
        <EmptyState label="No graph data" />
      )}
      <div className="absolute bottom-1.5 right-2 z-10 rounded bg-background/70 px-1.5 py-0.5 font-mono text-3xs text-muted-foreground backdrop-blur">
        {nodes.length} · {edges.length}
      </div>
      {hasData && legend.length > 0 && (
        <div className="absolute bottom-1.5 left-2 z-10 flex max-w-[70%] flex-wrap items-center gap-x-1.5 gap-y-0.5 rounded bg-background/70 px-1.5 py-0.5 backdrop-blur">
          {shownLegend.map(entry => (
            <span
              key={entry.label}
              className="flex items-center gap-1 text-4xs leading-none text-muted-foreground"
              title={`${entry.label} · ${entry.count}`}
            >
              <span
                className={cn(
                  'inline-block h-1.5 w-1.5 shrink-0 rounded-full',
                  entityColorClass(entry.label).replace('text-', 'bg-')
                )}
              />
              <span className="truncate text-foreground/80">{entry.label}</span>
              <span className="font-mono opacity-70">{entry.count}</span>
            </span>
          ))}
          {hiddenLegend > 0 && (
            <span className="font-mono text-4xs leading-none text-muted-foreground">
              +{hiddenLegend}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

interface PositionedNode {
  id: string
  x: number
  y: number
  r: number
  label: string | undefined
}
interface PositionedEdge {
  id: string
  x1: number
  y1: number
  x2: number
  y2: number
}

interface LegendEntry {
  label: string
  count: number
}

function buildLegend(positioned: PositionedNode[]): LegendEntry[] {
  const counts = new Map<string, number>()
  for (const node of positioned) {
    const key = node.label && node.label.trim() ? node.label : 'Other'
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  return [...counts.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count)
}

function computeLayout(
  nodes: GraphViewNode[],
  edges: GraphViewEdge[],
  limit: number
): { nodes: PositionedNode[]; edges: PositionedEdge[] } {
  if (nodes.length === 0) return { nodes: [], edges: [] }

  const degree = new Map<string, number>()
  for (const e of edges) {
    degree.set(e.source, (degree.get(e.source) ?? 0) + 1)
    degree.set(e.target, (degree.get(e.target) ?? 0) + 1)
  }
  const ordered = [...nodes].sort((a, b) => (degree.get(b.id) ?? 0) - (degree.get(a.id) ?? 0))
  const limited = ordered.slice(0, limit)

  const cx = VB_W / 2
  const cy = VB_H / 2
  const positions = new Map<string, PositionedNode>()
  limited.forEach((node, i) => {
    if (i === 0) {
      positions.set(node.id, { id: node.id, x: cx, y: cy, r: 4.5, label: node.typeLabel })
      return
    }
    const ring = Math.floor((i - 1) / 8) + 1
    const idxInRing = (i - 1) % 8
    const angle = (idxInRing / 8) * Math.PI * 2 + ring * 0.5
    const radius = 16 + ring * 16
    positions.set(node.id, {
      id: node.id,
      x: cx + Math.cos(angle) * radius,
      y: cy + Math.sin(angle) * radius * 0.62,
      r: ring === 1 ? 3.4 : 2.6,
      label: node.typeLabel,
    })
  })

  const drawnEdges: PositionedEdge[] = []
  for (const e of edges) {
    const a = positions.get(e.source)
    const b = positions.get(e.target)
    if (!a || !b) continue
    drawnEdges.push({ id: e.id, x1: a.x, y1: a.y, x2: b.x, y2: b.y })
  }
  return { nodes: [...positions.values()], edges: drawnEdges }
}

function EmptyState({ label }: { label: string }) {
  return (
    <div className="flex h-full w-full items-center justify-center text-2xs text-muted-foreground">
      {label}
    </div>
  )
}
